/*
  Curious Custom Studio - buy-box form engine
  ------------------------------------------------------------------
  Shared by the three custom product sections:
    custom-product-main.liquid          (phone cases)
    custom-product-main-magnet.liquid   (6 x 8 cm photo fridge magnets)
    custom-product-main-badge.liquid    (5.3 cm round shirt badges)

  IMPORTANT - DO NOT CONVERT THESE FORMS TO AJAX.
  Shopify's Ajax cart API cannot carry a file upload. These forms post
  natively (enctype="multipart/form-data") so that the customer artwork
  is attached to the line item. Wrapping them in <product-form> or
  routing them through /cart/add.js drops the file silently: the order
  still succeeds, with no artwork on it.

  The engine owns validation for these forms. The markup carries
  `novalidate` so that every message is rendered inline and visible,
  rather than relying on native bubbles (two of the three file inputs
  are visually hidden, which makes native validation unfocusable).

  Configuration is read from data attributes on the <form>:
    data-ccs-form            marks the form for this engine
    data-ccs-has-product     "true" when a real Shopify product is assigned
    data-ccs-max-mb          per-file upload ceiling, in MB
    data-ccs-accept          comma separated extension allow-list
    data-ccs-min-qty         minimum order quantity

  Two-step brand/model selection (phone cases):
    a <select data-ccs-brand-filter> narrows a <select data-ccs-variant-input>
    whose <option>s each carry data-brand. The model list stays disabled until
    a brand is chosen, so a customer never scrolls 40 models to find theirs.

  Add to Cart + Buy it Now, on one form:
    Shopify's own Dynamic Checkout button ({{ form | payment_button }},
    used elsewhere in this theme) submits straight to checkout through
    Shopify's own script - it never runs through this engine's validation,
    so a customer could buy a phone case with no artwork and no rights
    confirmation. That is not acceptable here, so "Buy it now" is instead a
    second <button type="submit" data-ccs-buy-now> in the SAME form. Both
    buttons go through the identical validate() gate and the identical
    native multipart POST; the only difference is a hidden
    <input data-ccs-return-to name="return_to"> that this engine sets to
    "/checkout" only when the buy-now button was the one clicked. Shopify's
    /cart/add endpoint honours return_to on a normal (non-AJAX) form post,
    so the browser lands on checkout instead of the cart page - with the
    artwork already attached to the line item, because it went through the
    same POST as Add to Cart.
*/

(function () {
  'use strict';

  var FORM_SELECTOR = 'form[data-ccs-form]';

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function extensionOf(filename) {
    var dot = String(filename).lastIndexOf('.');
    return dot === -1 ? '' : String(filename).slice(dot).toLowerCase();
  }

  function formatSize(bytes) {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return Math.max(1, Math.round(bytes / 1024)) + ' KB';
  }

  function readableList(items) {
    if (items.length <= 1) return items.join('');
    return items.slice(0, -1).join(', ') + ' or ' + items[items.length - 1];
  }

  function CustomStudioForm(form) {
    this.form = form;
    this.errorBox = form.querySelector('[data-ccs-errors]');
    this.submitButtons = toArray(form.querySelectorAll('[type="submit"]'));
    this.returnToInput = form.querySelector('[data-ccs-return-to]');
    this.lastSubmitter = null;
    this.variantIdInput = form.querySelector('input[name="id"]');
    this.consentInput = form.querySelector('[data-ccs-consent]');
    this.quantityInput = form.querySelector('input[name="quantity"]');
    this.fileInputs = toArray(form.querySelectorAll('input[type="file"]'));
    this.variantInputs = toArray(form.querySelectorAll('[data-ccs-variant-input]'));
    this.brandSelect = form.querySelector('[data-ccs-brand-filter]');
    this.modelSelect = this.variantInputs.filter(function (input) {
      return input.tagName === 'SELECT';
    })[0] || null;

    // Captured once, before the brand filter ever runs, so every branded
    // <option> can be put back in its original order later. Native <select>
    // pickers on iOS/iPadOS Safari do not reliably hide an <option> marked
    // `hidden` - the whole list still shows in the picker sheet. Actually
    // adding and removing the nodes is the one approach every browser
    // respects, so that is what applyBrandFilter does below.
    this.modelOptionsAll = this.modelSelect
      ? toArray(this.modelSelect.querySelectorAll('option[data-brand]'))
      : [];

    var scope = form.closest('.ccs-product-main-section, .ccs-product-main-wrapper, section') || document;
    this.priceTarget = scope.querySelector('[data-ccs-price]');

    this.hasProduct = form.getAttribute('data-ccs-has-product') === 'true';
    this.maxBytes = (parseFloat(form.getAttribute('data-ccs-max-mb')) || 20) * 1024 * 1024;
    this.maxLabel = (parseFloat(form.getAttribute('data-ccs-max-mb')) || 20) + 'MB';
    this.minQty = parseInt(form.getAttribute('data-ccs-min-qty'), 10) || 1;

    this.accepted = (form.getAttribute('data-ccs-accept') || '')
      .split(',')
      .map(function (ext) { return ext.trim().toLowerCase(); })
      .filter(function (ext) { return ext.charAt(0) === '.'; });

    this.bind();
    this.applyBrandFilter(true);
    this.syncVariant(true);
  }

  CustomStudioForm.prototype.bind = function () {
    var self = this;

    // Every submit button decides here whether this trip through /cart/add
    // ends on the cart page (Add to Cart) or goes straight to checkout
    // (Buy it now) - by arming or clearing the hidden return_to field.
    // click fires before submit, so this always runs first.
    this.submitButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        self.lastSubmitter = button;
        if (self.returnToInput) {
          self.returnToInput.value = button.hasAttribute('data-ccs-buy-now')
            ? (button.getAttribute('data-ccs-checkout-url') || '/checkout')
            : '';
        }
      });
    });

    this.form.addEventListener('submit', function (event) {
      var errors = self.validate();
      if (errors.length) {
        event.preventDefault();
        self.showErrors(errors);
        return;
      }
      self.clearErrors();
      // event.submitter is the reliable source (covers Enter-key implicit
      // submission too); lastSubmitter is a fallback for older browsers.
      self.lockSubmit(event.submitter || self.lastSubmitter);
    });

    this.variantInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        self.syncVariant(false);
        self.clearErrors();
      });
    });

    if (this.brandSelect) {
      this.brandSelect.addEventListener('change', function () {
        self.applyBrandFilter(false);
        self.syncVariant(false);
        self.clearErrors();
      });
    }

    this.fileInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var problems = self.checkFiles(input);
        if (problems.length) {
          input.value = '';
          self.showErrors(problems);
          self.resetDropzone(input);
        } else {
          self.clearErrors();
        }
      });
    });

    if (this.consentInput) {
      var consentBox = this.consentInput.closest('[data-ccs-consent-box]');
      var reflectConsent = function () {
        var checked = self.consentInput.checked;

        if (consentBox) {
          consentBox.classList.toggle('is-checked', checked);
          if (checked) consentBox.classList.remove('has-error');

          // The badge stops saying "tick this" the moment it's ticked -
          // otherwise a checked box still looks like it's waiting on you.
          var badge = consentBox.querySelector('[data-ccs-consent-badge]');
          if (badge) {
            badge.classList.toggle('is-confirmed', checked);
            var pending = badge.querySelector('[data-badge-pending]');
            var confirmed = badge.querySelector('[data-badge-confirmed]');
            if (pending) pending.hidden = checked;
            if (confirmed) confirmed.hidden = !checked;
          }
        }

        if (checked) self.clearErrors();
      };
      this.consentInput.addEventListener('change', reflectConsent);
      reflectConsent();
    }
  };

  /* ---------------------------------------------------------------
     Variant resolution - the selected option drives the hidden id,
     the displayed price and the availability of the submit button.
  ---------------------------------------------------------------- */

  CustomStudioForm.prototype.selectedVariantElement = function () {
    var chosen = null;

    this.variantInputs.forEach(function (input) {
      if (input.tagName === 'SELECT') {
        if (input.selectedIndex >= 0) {
          var option = input.options[input.selectedIndex];
          if (option && option.getAttribute('data-variant-id')) chosen = option;
        }
      } else if (input.checked) {
        chosen = input;
      }
    });

    return chosen;
  };

  CustomStudioForm.prototype.syncVariant = function (isInitial) {
    if (!this.hasProduct) {
      this.disableSubmit('Not available yet');
      return;
    }

    var element = this.selectedVariantElement();
    if (!element) {
      if (!isInitial) this.enableSubmit();
      return;
    }

    var id = element.getAttribute('data-variant-id');
    var price = element.getAttribute('data-variant-price');
    var available = element.getAttribute('data-variant-available') !== 'false';

    if (id && this.variantIdInput) this.variantIdInput.value = id;
    if (price && this.priceTarget) this.priceTarget.textContent = price;

    if (available) {
      this.enableSubmit();
    } else {
      this.disableSubmit('Sold out');
    }
  };

  /* ---------------------------------------------------------------
     Brand filter - narrows the model list to one manufacturer
  ---------------------------------------------------------------- */

  CustomStudioForm.prototype.applyBrandFilter = function (isInitial) {
    if (!this.brandSelect || !this.modelSelect) return;

    var select = this.modelSelect;
    var brand = this.brandSelect.value;
    var previousValue = select.value;
    var placeholder = select.options[0];
    var matches = 0;
    var restoredSelection = false;

    // Pull every branded option out of the DOM. The placeholder (the only
    // option with no data-brand) is left alone.
    toArray(select.querySelectorAll('option[data-brand]')).forEach(function (option) {
      option.remove();
    });

    // Put back only the options for the chosen brand, in their original
    // order, re-using the same nodes so their data-* attributes travel
    // with them untouched.
    this.modelOptionsAll.forEach(function (option) {
      if (brand === '' || option.getAttribute('data-brand') !== brand) return;

      var soldOut = option.getAttribute('data-variant-available') === 'false';
      option.hidden = false;
      option.disabled = soldOut;
      select.appendChild(option);

      if (!soldOut) matches++;
      if (option.value === previousValue) {
        option.selected = true;
        restoredSelection = true;
      }
    });

    // A model left over from another brand must not stay selected.
    if (!restoredSelection) select.selectedIndex = 0;

    select.disabled = brand === '';

    if (placeholder && !placeholder.getAttribute('data-brand')) {
      if (brand === '') {
        placeholder.textContent = select.getAttribute('data-ccs-empty-label') || 'Choose a brand first';
      } else if (matches === 0) {
        placeholder.textContent = 'No models available for ' + brand;
      } else {
        placeholder.textContent = select.getAttribute('data-ccs-placeholder') || 'Choose your exact model';
      }
    }

    if (!isInitial) this.clearErrors();
  };

  /* ---------------------------------------------------------------
     Validation
  ---------------------------------------------------------------- */

  CustomStudioForm.prototype.checkFiles = function (input) {
    var self = this;
    var errors = [];
    var files = toArray(input.files);
    var label = input.getAttribute('data-ccs-file-label') || 'artwork';

    files.forEach(function (file) {
      if (file.size > self.maxBytes) {
        errors.push(
          '"' + file.name + '" is ' + formatSize(file.size) + '. The limit is ' +
          self.maxLabel + ' per file - upload a smaller version, or send it to us after ordering.'
        );
        return;
      }

      if (self.accepted.length) {
        var ext = extensionOf(file.name);
        if (self.accepted.indexOf(ext) === -1) {
          errors.push(
            '"' + file.name + '" is not a supported file type. Upload ' +
            readableList(self.accepted.map(function (e) { return e.replace('.', '').toUpperCase(); })) +
            ' for your ' + label + '.'
          );
        }
      }
    });

    return errors;
  };

  CustomStudioForm.prototype.validate = function () {
    var self = this;
    var errors = [];

    if (!this.hasProduct) {
      return ['This product is not connected to Shopify yet, so it cannot be added to the cart.'];
    }

    // Brand comes before model, so ask for it first and stop there.
    if (this.brandSelect && this.brandSelect.value === '') {
      return [this.brandSelect.getAttribute('data-ccs-required-message') || 'Choose your phone brand before adding to cart.'];
    }

    // Required selections (phone model, pack size, backing type...)
    this.variantInputs.forEach(function (input) {
      if (input.tagName !== 'SELECT') return;
      var option = input.options[input.selectedIndex];
      if (!option || !option.getAttribute('data-variant-id')) {
        errors.push(input.getAttribute('data-ccs-required-message') || 'Choose an option before adding to cart.');
      }
    });

    if (this.variantInputs.length && !this.selectedVariantElement()) {
      var radioMessage = this.form.getAttribute('data-ccs-variant-message');
      if (radioMessage && errors.indexOf(radioMessage) === -1) errors.push(radioMessage);
    }

    // Artwork
    this.fileInputs.forEach(function (input) {
      var required = input.getAttribute('data-ccs-require-file') === 'true';
      var label = input.getAttribute('data-ccs-file-label') || 'artwork';

      if (required && (!input.files || input.files.length === 0)) {
        errors.push('Upload your ' + label + ' before adding to cart.');
        return;
      }
      errors = errors.concat(self.checkFiles(input));
    });

    // Artwork rights consent - required before add-to-cart.
    if (this.consentInput && !this.consentInput.checked) {
      errors.push('Confirm that you own this image or have permission to reproduce it.');
    }

    // Quantity
    if (this.quantityInput) {
      var qty = parseInt(this.quantityInput.value, 10);
      if (isNaN(qty) || qty < this.minQty) {
        errors.push('The minimum order is ' + this.minQty + '.');
      }
    }

    return errors;
  };

  /* ---------------------------------------------------------------
     Feedback
  ---------------------------------------------------------------- */

  CustomStudioForm.prototype.showErrors = function (errors) {
    if (!this.errorBox || !errors.length) return;

    // Flag the consent box itself when it's the reason for the block, so
    // the customer isn't left hunting the page for which field the error
    // list is talking about.
    if (this.consentInput) {
      var consentBox = this.consentInput.closest('[data-ccs-consent-box]');
      if (consentBox) consentBox.classList.toggle('has-error', !this.consentInput.checked);
    }

    this.errorBox.innerHTML = '';

    var heading = document.createElement('p');
    heading.className = 'ccs-form-error-title';
    heading.textContent = errors.length === 1 ? 'One thing to fix first' : 'A few things to fix first';
    this.errorBox.appendChild(heading);

    var list = document.createElement('ul');
    list.className = 'ccs-form-error-list';
    errors.forEach(function (message) {
      var item = document.createElement('li');
      item.textContent = message;
      list.appendChild(item);
    });
    this.errorBox.appendChild(list);

    this.errorBox.hidden = false;
    this.errorBox.setAttribute('tabindex', '-1');
    this.errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.errorBox.focus({ preventScroll: true });
  };

  CustomStudioForm.prototype.clearErrors = function () {
    if (!this.errorBox) return;
    this.errorBox.innerHTML = '';
    this.errorBox.hidden = true;
  };

  CustomStudioForm.prototype.resetDropzone = function (input) {
    var zone = input.closest('.ccs-upload-dropzone');
    if (!zone) return;
    zone.classList.remove('has-file', 'has-files');
    var feedback = zone.querySelector('.ccs-dropzone-feedback');
    if (feedback) feedback.style.display = 'none';
    var filename = zone.querySelector('.ccs-upload-filename');
    if (filename) {
      filename.textContent = '';
      filename.style.display = 'none';
    }
  };

  CustomStudioForm.prototype.disableSubmit = function (label) {
    // Neither button can be used while the product/variant is unavailable,
    // so both say why - a "Buy it now" that still looked clickable next to
    // a disabled "Add to Cart" would be confusing.
    this.submitButtons.forEach(function (button) {
      if (!button.hasAttribute('data-ccs-label')) {
        button.setAttribute('data-ccs-label', button.textContent.trim());
      }
      button.disabled = true;
      button.classList.add('is-disabled');
      button.textContent = label;
    });
  };

  CustomStudioForm.prototype.enableSubmit = function () {
    this.submitButtons.forEach(function (button) {
      var original = button.getAttribute('data-ccs-label');
      button.disabled = false;
      button.classList.remove('is-disabled');
      if (original) button.textContent = original;
    });
  };

  CustomStudioForm.prototype.lockSubmit = function (submitter) {
    // The page navigates on a native POST; this only prevents a double
    // submit while the file uploads. Both buttons lock, but only the one
    // actually clicked changes its label - the other stays readable while
    // greyed out, rather than both claiming to be "uploading".
    var active = submitter || this.submitButtons[0];
    this.submitButtons.forEach(function (button) {
      if (!button.hasAttribute('data-ccs-label')) {
        button.setAttribute('data-ccs-label', button.textContent.trim());
      }
      button.disabled = true;
      button.classList.add('is-loading');
      if (button === active) button.textContent = 'Uploading your artwork...';
    });
  };

  function initAll(root) {
    toArray((root || document).querySelectorAll(FORM_SELECTOR)).forEach(function (form) {
      if (form.ccsFormEngine) return;
      form.ccsFormEngine = new CustomStudioForm(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
